import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { useAppSelector } from '@/hooks/useRedux';
import { config } from '@/config/env';

/**
 * Organization interface representing an FPO the user belongs to
 */
export interface Organization {
  id: string;
  name: string;
}

interface OrganizationContextType {
  selectedOrg: Organization | null;
  availableOrgs: Organization[];
  selectOrganization: (orgId: string) => void;
  isLoading: boolean;
  error: string | null;
}

const OrganizationContext = createContext<OrganizationContextType | undefined>(undefined);

export const useOrganization = () => {
  const context = useContext(OrganizationContext);
  if (!context) {
    throw new Error('useOrganization must be used within OrganizationProvider');
  }
  return context;
};

interface OrganizationProviderProps {
  children: ReactNode;
}

/**
 * OrganizationProvider fetches available organizations from AAA service
 * and manages the currently selected organization for multi-tenant access.
 *
 * Flow:
 * 1. LOGIN -> User authenticated with JWT
 * 2. FETCH USER ORGANIZATIONS -> GET /api/v1/me/organizations (uses user_id from JWT, no special permission required)
 * 3. AUTO-SELECT -> First organization is auto-selected
 * 4. Store org_id in localStorage as 'organizationId'
 */
export const OrganizationProvider: React.FC<OrganizationProviderProps> = ({ children }) => {
  const user = useAppSelector((state) => state.auth.user);
  const accessToken = useAppSelector((state) => state.auth.accessToken);

  const [availableOrgs, setAvailableOrgs] = useState<Organization[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedOrgId, setSelectedOrgId] = useState<string | null>(() => {
    // Try to restore from localStorage on mount
    return localStorage.getItem('organizationId');
  });

  /**
   * Fetch user's organizations from AAA service
   * Uses /api/v1/me/organizations endpoint which extracts user_id from JWT
   * No special permission required - any authenticated user can access their own organizations
   */
  const fetchUserOrganizations = useCallback(async () => {
    if (!accessToken) {
      console.log('[OrganizationContext] No access token, skipping org fetch');
      return;
    }

    console.log('[OrganizationContext] Fetching organizations for authenticated user');
    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch(
        `${config.authServiceUrl}/api/v1/me/organizations`,
        {
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch organizations: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      console.log('[OrganizationContext] Raw API response:', JSON.stringify(data, null, 2));

      if (data.success && data.data?.organizations) {
        // Map the response to Organization interface
        // Service returns: [{ id: "ORGN001", name: "FPO Name" }, ...]
        const orgs: Organization[] = data.data.organizations.map((org: any) => ({
          id: org.id,
          name: org.name || `Organization ${org.id}`,
        }));

        console.log('[OrganizationContext] Found organizations:', orgs);
        setAvailableOrgs(orgs);

        // Auto-select first organization if none selected
        if (orgs.length > 0 && !selectedOrgId) {
          const autoSelectedId = orgs[0].id;
          console.log('[OrganizationContext] Auto-selecting first organization:', autoSelectedId);
          setSelectedOrgId(autoSelectedId);
          localStorage.setItem('organizationId', autoSelectedId);
        }
      } else {
        console.warn('[OrganizationContext] Unexpected response format:', data);
        setAvailableOrgs([]);
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error fetching organizations';
      console.error('[OrganizationContext] Error fetching organizations:', err);
      setError(errorMessage);
      setAvailableOrgs([]);
    } finally {
      setIsLoading(false);
    }
  }, [accessToken, selectedOrgId]);

  // Fetch organizations when user logs in
  useEffect(() => {
    if (user && accessToken) {
      fetchUserOrganizations();
    }
  }, [user, accessToken, fetchUserOrganizations]);

  // Clear organization state when user logs out
  useEffect(() => {
    if (!user) {
      console.log('[OrganizationContext] No user, clearing organization state');
      setSelectedOrgId(null);
      setAvailableOrgs([]);
      setError(null);
      localStorage.removeItem('organizationId');
      // Also clear FPO config caches
      Object.keys(localStorage)
        .filter(k => k.startsWith('fpo_config'))
        .forEach(k => localStorage.removeItem(k));
    }
  }, [user]);

  // Validate that selected org is still in available list
  useEffect(() => {
    if (selectedOrgId && availableOrgs.length > 0) {
      const stillValid = availableOrgs.some(org => org.id === selectedOrgId);
      if (!stillValid) {
        console.warn('[OrganizationContext] Selected org no longer valid, resetting to first available');
        const newOrgId = availableOrgs[0].id;
        setSelectedOrgId(newOrgId);
        localStorage.setItem('organizationId', newOrgId);
      }
    }
  }, [availableOrgs, selectedOrgId]);

  // Find the currently selected organization
  const selectedOrg = selectedOrgId
    ? availableOrgs.find(org => org.id === selectedOrgId) || null
    : availableOrgs[0] || null;

  // Log state for debugging
  useEffect(() => {
    console.log('[OrganizationContext] State update - selectedOrg:', selectedOrg?.id, 'availableOrgs:', availableOrgs.length);
  }, [selectedOrg, availableOrgs]);

  const selectOrganization = useCallback((orgId: string) => {
    console.log('[OrganizationContext] Selecting organization:', orgId);
    setSelectedOrgId(orgId);
    localStorage.setItem('organizationId', orgId);
  }, []);

  const value: OrganizationContextType = {
    selectedOrg,
    availableOrgs,
    selectOrganization,
    isLoading,
    error,
  };

  return (
    <OrganizationContext.Provider value={value}>
      {children}
    </OrganizationContext.Provider>
  );
};
